import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataTable, ExportMenu, FilterBar, type FilterSpec } from "@/components/data";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";
import { Combobox } from "../Combobox";
import { getProduction, getProducts, getRecords, messageFor, PcogsError, type ProductionDetail, type RecordRow } from "../api";
import { recordsCsv, recordsFilename } from "../csv";
import { ENTITIES, money, pct, shortDate } from "../format";
import { canDrill, channelWords, costWords, pvrOptions, qtyWords, searchRows, skuOptions, sortByDate } from "../records";
import { ENTITY_SPEC, isEntityFilter } from "../scope";
import { SkuHistory, type SkuHistorySelection } from "./SkuHistory";

const choice = (name: keyof ReturnType<typeof pvrOptions>, label: string, options: string[]): FilterSpec => ({
  name,
  label,
  kind: "choice",
  anyLabel: "All",
  options: options.map((value) => ({ value, label: value })),
});

export function RecordsTab({ areaName: _areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const parsed = parseFilters(search);
  const entity = isEntityFilter(parsed.entity) ? parsed.entity : "all";
  const since = typeof parsed.since === "string" ? parsed.since : "";
  const until = typeof parsed.until === "string" ? parsed.until : "";
  const q = typeof search.q === "string" ? search.q : "";
  const values: FilterValues = parsed;
  const [pageIndex, setPageIndex] = useState(0);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [productionKey, setProductionKey] = useState<string | null>(null);
  const [skuHistory, setSkuHistory] = useState<SkuHistorySelection | null>(null);

  const products = useQuery({ queryKey: ["pcogs", "products", "all"], queryFn: () => getProducts(null, null, false) });
  const options = useMemo(() => pvrOptions(products.data?.data ?? []), [products.data]);
  const filters = useMemo<FilterSpec[]>(() => [
    ENTITY_SPEC,
    choice("tracking", "Product", options.tracking),
    choice("variant", "Variant", options.variant),
    choice("location", "Region", options.location),
  ], [options]);
  const records = useQuery({
    queryKey: ["pcogs", "records", entity, values.sku ?? "", values.tracking ?? "", values.variant ?? "", values.location ?? "", since, until, 5000],
    queryFn: () => getRecords({
      entity,
      sku: typeof values.sku === "string" ? values.sku : null,
      tracking: typeof values.tracking === "string" ? values.tracking : null,
      variant: typeof values.variant === "string" ? values.variant : null,
      location: typeof values.location === "string" ? values.location : null,
      since: since || null,
      until: until || null,
      limit: 5000,
    }),
  });
  const rows = useMemo(() => sortByDate(searchRows(records.data?.data ?? [], products.data?.data ?? [], q), sortDirection), [products.data, q, records.data, sortDirection]);
  const pageSize = 100;
  const pageRows = rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  const changeFilters = (next: FilterValues) => {
    setPageIndex(0);
    void navigate({ search: applyFilters(search, next) as never, replace: true });
  };
  const changeQuery = (next: string) => {
    setPageIndex(0);
    const nextSearch = { ...search };
    if (next) nextSearch.q = next;
    else delete nextSearch.q;
    void navigate({ search: nextSearch as never, replace: true });
  };
  const downloadCsv = () => {
    const url = URL.createObjectURL(new Blob([recordsCsv(rows, products.data?.data ?? [])], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = recordsFilename(entity, since, until);
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const columns = useMemo<ColumnDef<RecordRow, unknown>[]>(() => [
    { id: "approved_date", header: "Date", cell: ({ row }) => shortDate(row.original.approved_date), meta: { sortable: true } },
    { header: "SKU", cell: ({ row }) => <Button data-nonmutating variant="link" className="px-0 font-medium" onClick={(event) => { event.stopPropagation(); setSkuHistory(row.original); }}>{row.original.sku}</Button> },
    { header: "Product", cell: ({ row }) => row.original.tracking ?? "-" },
    { header: "Variant", cell: ({ row }) => row.original.variant ?? "-" },
    { header: "Region", cell: ({ row }) => row.original.location ?? "-" },
    { header: "Channel", cell: ({ row }) => channelWords(row.original.sku, products.data?.data ?? []) },
    { header: "Production", cell: ({ row }) => canDrill(row.original)
      ? <Button data-nonmutating variant="link" className="px-0 font-medium" onClick={(event) => { event.stopPropagation(); setProductionKey(row.original.production_key); }}>{row.original.production_key}</Button>
      : <Badge variant="outline">Legacy</Badge> },
    { header: "Qty", cell: ({ row }) => qtyWords(row.original), meta: { align: "right" } },
    { header: "Cost", cell: ({ row }) => costWords(row.original), meta: { align: "right" } },
    { header: "Ccy", cell: ({ row }) => row.original.book_currency },
    { header: "vs prev", cell: ({ row }) => pct(row.original.vs_previous_pct), meta: { align: "right" } },
    { header: "Entity", cell: ({ row }) => ENTITIES[row.original.entity_id].short },
  ], [products.data]);
  const emptyEntity = entity === "all" ? "all entities" : ENTITIES[entity].name;
  const error = records.error ?? products.error;

  return <div className="space-y-5">
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <FilterBar filters={filters} values={values} onChange={changeFilters} clears={["sku", "since", "until"]} layout="inline" trailing={<div className="flex flex-wrap items-end gap-2"><div className="w-72"><Label className="sr-only">SKU</Label><Combobox value={typeof values.sku === "string" ? values.sku : ""} onValueChange={(sku) => changeFilters({ ...values, sku: sku || null })} options={[{ value: "", label: "Any SKU", hint: "" }, ...skuOptions(products.data?.data ?? [])]} placeholder="Any SKU" emptyText="No SKU found." disabled={products.isPending} /></div><DateRange since={since} until={until} onChange={(range) => changeFilters({ ...values, ...range })} /></div>} />
      </div>
      <ExportMenu decision="view" formats={[{ key: "csv", label: "Export CSV" }]} onExport={downloadCsv} />
    </div>
    <div className="max-w-xl"><Label htmlFor="records-search" className="sr-only">Search records</Label><Input id="records-search" type="search" placeholder="Search SKU, product, variant, channel" value={q} onChange={(event) => changeQuery(event.target.value)} /></div>
    {error
      ? <ReadError error={error} retry={() => { void records.refetch(); void products.refetch(); }} />
      : <DataTable columns={columns} rows={pageRows} page={{ pageIndex, pageSize, rowCount: rows.length }} onPageChange={setPageIndex} sort={{ id: "approved_date", direction: sortDirection }} onSort={(id) => { if (id === "approved_date") { setSortDirection((current) => current === "desc" ? "asc" : "desc"); setPageIndex(0); } }} pending={records.isPending || products.isPending} emptyMessage={`No records match. ${emptyEntity}${since || until ? `, ${since ? shortDate(since) : "first date"} to ${until ? shortDate(until) : "latest"}` : ", all dates"}.`} density="compact" />}
    {skuHistory && <SkuHistory selected={skuHistory} onClose={() => setSkuHistory(null)} onProductionOpen={(key) => { setSkuHistory(null); setProductionKey(key); }} />}
    <RecordDrillIn productionKey={productionKey} onClose={() => setProductionKey(null)} />
  </div>;
}

function DateRange({ since, until, onChange }: { since: string; until: string; onChange: (range: { since: string; until: string }) => void }) {
  return <span className="inline-flex flex-wrap items-end gap-2">
    <Label className="grid gap-1">Since<Input type="date" value={since} onChange={(event) => onChange({ since: event.target.value, until })} /></Label>
    <Label className="grid gap-1">Until<Input type="date" value={until} onChange={(event) => onChange({ since, until: event.target.value })} /></Label>
  </span>;
}

function RecordDrillIn({ productionKey, onClose }: { productionKey: string | null; onClose: () => void }) {
  const production = useQuery({ queryKey: ["pcogs", "production", productionKey], queryFn: () => getProduction(productionKey!), enabled: Boolean(productionKey) });
  const detail = production.data?.data;
  return <Sheet open={Boolean(productionKey)} onOpenChange={(open) => { if (!open) onClose(); }}>
    <SheetContent id="d-recdrill" className="overflow-y-auto sm:max-w-2xl">
      <SheetHeader><SheetTitle>{productionKey ?? "Production"}</SheetTitle><SheetDescription>{detail ? `${ENTITIES[detail.production.entity_id].name} production figures in ${ENTITIES[detail.production.entity_id].currency}.` : "Production lines and cost summary."}</SheetDescription></SheetHeader>
      <div className="space-y-5 px-4 pb-4">
        {production.error && <ReadError error={production.error} retry={() => { void production.refetch(); }} />}
        {production.isPending && <p>Loading production...</p>}
        {detail && <ProductionFigures detail={detail} />}
        {productionKey && <Button data-nonmutating variant="outline" asChild><a href={`/app/margin_engine/pcogs/runs?f_run=${encodeURIComponent(productionKey)}`}>Open in Runs</a></Button>}
      </div>
    </SheetContent>
  </Sheet>;
}

function ProductionFigures({ detail }: { detail: ProductionDetail }) {
  const currency = ENTITIES[detail.production.entity_id].currency;
  return <>
    <section className="space-y-2"><h3 className="font-bold">Production lines</h3><Table><TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Declared qty</TableHead><TableHead>Unit cost</TableHead><TableHead>Total</TableHead></TableRow></TableHeader><TableBody>{detail.lines.map((line) => <TableRow key={line.sku}><TableCell>{line.sku}</TableCell><TableCell>{line.declared_qty ?? "-"}</TableCell><TableCell>{money(line.unit_cost, currency, 4)}</TableCell><TableCell>{money(line.total_value, currency)}</TableCell></TableRow>)}</TableBody></Table></section>
    <section className="space-y-2"><h3 className="font-bold">Cost summary by account</h3><Table><TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Account</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader><TableBody>{detail.cost_summary.flatMap((summary) => Object.entries(summary.by_account).map(([account, amount]) => <TableRow key={`${summary.sku}:${account}`}><TableCell>{summary.sku}</TableCell><TableCell>{account}</TableCell><TableCell>{money(amount, currency)}</TableCell></TableRow>))}</TableBody></Table></section>
  </>;
}

function ReadError({ error, retry }: { error: unknown; retry: () => void }) {
  if (error instanceof PcogsError && error.status === 403) return <Alert variant="destructive"><AlertTitle>We can't confirm your access right now.</AlertTitle><AlertDescription>{typeof error.body?.details.reason === "string" ? error.body.details.reason : null}</AlertDescription></Alert>;
  if (error instanceof PcogsError && error.status === 401) return <Alert variant="destructive"><AlertDescription>Your sign-in has expired. Reload the page to sign in again; unsaved values in this form will be lost. <Button data-nonmutating variant="outline" size="sm" onClick={() => window.location.reload()}>Reload</Button></AlertDescription></Alert>;
  return <Alert variant="destructive"><AlertDescription>{messageFor(error)} <Button data-nonmutating variant="outline" size="sm" onClick={retry}>Retry</Button></AlertDescription></Alert>;
}
